import React, { useCallback, useState, useEffect } from 'react';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { Grid, Typography, Link, Button, TextField, Card, CardContent } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Skeleton from '@material-ui/lab/Skeleton';
import { DataGridCard, VerticalBarCard, RateAnalysisChart } from '../../../../HOC/Cards';
import { InfoTooltip } from 'src/App/HOC/Tooltips';
import { renderCellExpand } from '../../../../Components';
import clsx from 'clsx';
import { GridCellParams, GridColDef } from '@mui/x-data-grid';
import {
  AverageStayLengths,
  BookingLeads,
  BookingRates,
  Compset,
  PacingData,
  RateAndAvailChanges,
} from 'src/interfaces/idashboard';
import { PricingListingViewPropTypes } from 'src/interfaces/iproptypes';
import {
  COLOR_PRIMARY_LIGHT,
  COLOR_PRIMARY_DARK,
  COLOR_ACCENT_1,
  COLOR_ACCENT_2,
  COLOR_GREEN,
  COLOR_GREY,
  COLOR_ORANGE,
  COLOR_RED,
  COLOR_TOOLTIP_BACKGROUND,
  exchangeRates,
} from 'src/const';
import Alert from '@material-ui/lab/Alert';
import DeleteForeverTwoToneIcon from '@material-ui/icons/DeleteForeverTwoTone';
import { convertCurrency } from 'src/utils/functions';

const d3 = require('d3');

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    '& .super-app-theme--header': {
      backgroundColor: COLOR_PRIMARY_LIGHT,
    },
    '& .super-app.booked': {
      backgroundColor: COLOR_ORANGE,
    },
    '& .super-app.recently_booked': {
      backgroundColor: COLOR_RED,
    },
    '& .super-app.not_booked': {
      backgroundColor: COLOR_GREEN,
    },
    '& .super-app.inactive': {
      backgroundColor: COLOR_GREY,
    },
  },
  explanationCard: {
    backgroundColor: '#ffffff',
    padding: 14,
    marginBottom: 10,
  },
  explnationText: {
    color: '#0A0A0A',
    fontSize: 16,
    fontWeight: 'bold',
  },
  verticlBarCard: {
    width: 300,
    height: 185,
  },
  verticalGraphSection: {
    width: '100%',
    marginTop: 15,
    marginBottom: 10,
  },
  customButton: {
    background: COLOR_PRIMARY_DARK, //`radial-gradient(circle, hsla(189, 96%, 29%, 1) 0%, hsla(178, 100%, 38%, 1) 100%);`,
    borderRadius: 5,
    border: 0,
    color: 'white',
    height: 40,
    padding: '0 15px',
    boxShadow: '0 1px 1px 1px rgba(255, 255, 255, .3)',
    '&:hover': {
      background: COLOR_PRIMARY_LIGHT,
    },
  },
  rateAnalysisChart: {
    width: '100%',
    marginTop: 10,
    marginBottom: 10,
  },
  skeleton: {
    marginTop: 10,
    marginBottom: 10,
  },
  alert: {
    width: '100%',
  },
  compsetNameText: {
    marginRight: 15,
    fontSize: 30,
    fontWeight: 400,
  },
}));

const ListingView = ({
  bookingChanges,
  filteredBookings,
  search,
  totalNumberOfListings,
  getBookingChanges,
  compsetFilterVal,
  setCompsetFilterVal,
  page,
  setPage,
  filterTriggered,
  compsets,
  compset,
  compsetListingIDs,
  getCompsetListingIdsLoadStart,
  setCompsetListingIds,
  compsetCRUD,
  compsetID,
  idle,
  setIdle,
  setFilterOpen,
  deletedRows,
  setDeletedRows,
  currentCompsetID,
  setCurrentCompsetID,
  currentCompsetName,
  setCurrentCompsetName,
  currency,
}: PricingListingViewPropTypes) => {
  const classes = useStyles();
  let lastSnapshot: string = '';
  let currentSnapshot: string = '';
  let nextUpdate: string = '';
  let dateList: string[] = [];
  let bookingChangesCols: GridColDef[] = [
    {
      field: 'title',
      headerName: 'Title',
      width: 400,
      renderCell: (params: any) => (
        <Link href={`https://airbnb.com/rooms/${params.row.listingID}`} target="_blank">
          <Typography>{params.value}</Typography>
        </Link>
      ),
    },
    { field: 'hyperlink', headerName: 'Hyperlink', width: 400, hide: true },
    { field: 'host_name', headerName: 'Host Name', width: 200, renderCell: renderCellExpand },
    { field: 'room_type', headerName: 'Room Type', width: 200 },
    { field: 'bedrooms', headerName: 'Bedrooms', width: 200 },
    { field: 'beds', headerName: 'Beds', width: 200 },
    { field: 'bathrooms', headerName: 'Bathrooms', width: 200 },
    { field: 'pool', headerName: 'Pool', width: 200 },
    { field: 'amenities', headerName: 'Amenities', width: 200, renderCell: renderCellExpand },
    { field: 'accommodates', headerName: 'Accommodates', width: 200 },
    { field: 'room_and_property_type', headerName: 'Room & Property Type', width: 200 },
    { field: 'house_type', headerName: 'House Type', width: 200 },
    {
      field: 'cleaning_fee',
      headerName: 'Cleaning Fee',
      width: 200,
      renderCell: (params: any) =>
        `${exchangeRates[currency].symbol}${convertCurrency(currency, params.value)}`,
    },
    {
      field: 'guest_fee',
      headerName: 'Guest Fee',
      width: 200,
      renderCell: (params: any) =>
        `${exchangeRates[currency].symbol}${convertCurrency(currency, params.value)}`,
    },
    { field: 'minimum_nights', headerName: 'Minimum Nights', width: 200 },
    { field: 'review_count', headerName: 'Reviews', width: 200 },
    { field: 'review_rate', headerName: 'Review Rate (per Month)', width: 200 },
    { field: 'listings_count', headerName: 'Listings Count', width: 200 },
  ];
  let dynamicAvailableDailyRates: BookingRates = {} as BookingRates;
  let dynamicOccupancyRates: BookingRates = {} as BookingRates;
  let dynamicBookingLeads: BookingLeads = {} as BookingLeads;
  let dynamicAverageStayLengths: AverageStayLengths = {} as AverageStayLengths;
  let dynamic365DaysAvailAndBookedDailyRates: PacingData[] = [];

  const date = new Date();
  const currentHour: number = date.getUTCHours();
  const currentMinute: number = date.getUTCMinutes();
  let lastUpdateHourDiff: number = 0;
  let nextUpdateHourDiff: number = 0;
  let lastUpdateMinuteDiff: number = 0;
  let nextUpdateMinuteDiff: number = 0;

  useEffect(() => {
    if (!compsetListingIDs.loading && compsetListingIDs.data.length > 0) {
      setFilterOpen(false);
    }
  }, [compsetListingIDs.data]);

  const handleCompsetChange = useCallback((compset: Compset) => {
    setIdle(false);
    const id: string = compset.id;
    setCurrentCompsetID(id);
    setCurrentCompsetName(compset.name);
    getCompsetListingIdsLoadStart();
    setCompsetListingIds(compset.listingIDs);
    setDeleteTriggered(false);
    getBookingChanges(1, compset.id);
    window.history.pushState({}, '', `/compset/${id}`);
  }, []);

  const loadAdditionalRows = () => {
    setPage((p: number) => {
      if (compsetID) getBookingChanges(p, compsetID);
      else if (currentCompsetID) getBookingChanges(p, currentCompsetID);
      else getBookingChanges(p, compset.data.id);
      return p + 1;
    });
  };

  const [deleteTriggered, setDeleteTriggered] = useState<boolean>(false);
  const deleteCompset = () => {
    if (compsetID) compsetCRUD('DELETE', null, compsetID);
    else compsetCRUD('DELETE', null, currentCompsetID);
    setIdle(true);
    setDeleteTriggered(true);
  };

  if (
    !bookingChanges.loading &&
    bookingChanges.data &&
    !filteredBookings.loading &&
    filteredBookings.data &&
    filteredBookings.data.length > 0
  ) {
    lastSnapshot = bookingChanges.data.last_snapshot;
    currentSnapshot = bookingChanges.data.current_snapshot;
    nextUpdate = bookingChanges.data.next_update;
    dateList = bookingChanges.data.date_list;
    [dynamicAvailableDailyRates, dynamicOccupancyRates] = getDynamicAvailableAndOccupancyRates(
      filteredBookings.data,
      dateList
    );
    dynamicBookingLeads = getDynamicBookingLeads(filteredBookings.data);
    dynamicAverageStayLengths = getDynamicAverageStays(filteredBookings.data);
    dynamic365DaysAvailAndBookedDailyRates = getDynamic365DaysAvailAndBookedDailyRates(
      filteredBookings.data,
      dateList,
      currency
    );

    const getCellRenderVal = (params: GridCellParams) => {
      const totalBookingVal: number = getTotalBookingVal(params, dateList);
      const bookingRate: string = params.value as string;
      if (params && (bookingRate.includes(':b') || bookingRate.includes(':rb'))) {
        return (
          <InfoTooltip
            title={
              <Card>
                <CardContent style={{ background: COLOR_TOOLTIP_BACKGROUND }}>
                  <Typography variant="body2" style={{ fontWeight: 500 }}>
                    Total Booking Value: {exchangeRates[currency].symbol}
                    {convertCurrency(currency, totalBookingVal)}
                    <br />
                    Total Rate/Capacity: {exchangeRates[currency].symbol}
                    {convertCurrency(
                      currency,
                      Math.round(totalBookingVal / params.row.accommodates)
                    )}
                  </Typography>
                </CardContent>
              </Card>
            }
          >
            <div style={{ width: '100%' }}>
              {parsePrice(currency, (params.value as string).replace(/:b|:rb|:a|:na/g, ''))}
            </div>
          </InfoTooltip>
        );
      } else {
        return (
          <div style={{ width: '100%' }}>
            {parsePrice(currency, (params.value as string).replace(/:b|:rb|:a|:na/g, ''))}
          </div>
        );
      }
    };

    for (let date of dateList) {
      let isWeekend: boolean = false;
      let dayOfWeek: number = new Date(date).getUTCDay();
      let headerName: string = `${date}`;
      if (dayOfWeek == 5) {
        headerName = headerName + ' (FRI)';
        isWeekend = true;
      }
      if (dayOfWeek == 6) {
        headerName = headerName + ' (SAT)';
        isWeekend = true;
      }

      bookingChangesCols.push({
        field: `${date}`,
        headerName: `${headerName}`,
        headerClassName: isWeekend ? 'super-app-theme--header' : '',
        width: 200,
        cellClassName: (params: GridCellParams) =>
          clsx('super-app', {
            booked: (params?.value as string).includes(':b'),
            recently_booked: (params?.value as string).includes(':rb'),
            not_booked: (params?.value as string).includes(':a'),
            inactive: (params?.value as string).includes(':na'),
          }),
        renderCell: getCellRenderVal,
      });
    }

    let currentSnapshotObj: string[] = currentSnapshot.split('-');
    let nextUpdateObj: string[] = nextUpdate.split('-');
    let weeklyLastUpdateDate: number = new Date(
      parseInt(currentSnapshotObj[0]),
      parseInt(currentSnapshotObj[1]) - 1,
      parseInt(currentSnapshotObj[2])
    ).setUTCHours(15);
    let weeklyNextUpdateDate = new Date(
      parseInt(nextUpdateObj[0]),
      parseInt(nextUpdateObj[1]) - 1,
      parseInt(nextUpdateObj[2])
    ).setUTCHours(15);

    let weeklyLastUpdateTimeDiff: number = Math.round(
      Math.abs(weeklyLastUpdateDate - date.getTime()) / 60000
    );
    let weeklyNextUpdateTimeDiff: number = Math.round(
      Math.abs(date.getTime() - weeklyNextUpdateDate) / 60000
    );
    lastUpdateHourDiff = Math.round(weeklyLastUpdateTimeDiff / 60);
    lastUpdateMinuteDiff = weeklyLastUpdateTimeDiff % 60;
    nextUpdateHourDiff = Math.round(weeklyNextUpdateTimeDiff / 60);
    nextUpdateMinuteDiff = weeklyNextUpdateTimeDiff % 60;
  }

  const deleteSelectedRows = () => {
    if (deletedRows && deletedRows.length > 0) {
      if (compsetID) compsetCRUD('UPDATE', null, compsetID, deletedRows);
      else if (currentCompsetID) compsetCRUD('UPDATE', null, currentCompsetID, deletedRows);
      else compsetCRUD('UPDATE', null, compset.data.id, deletedRows);
    }
  };

  return (
    <Grid container direction="column" alignItems="flex-end" spacing={6} style={{ marginTop: 20 }}>
      <Grid
        container
        direction="row"
        justify="space-between"
        className={classes.verticalGraphSection}
      >
        <Grid style={{ width: 600 }}>
          {!compset.loading && !currentCompsetName ? (
            <Typography className={classes.compsetNameText} display="inline">
              {compset.data.name}
            </Typography>
          ) : (
            <Typography className={classes.compsetNameText} display="inline">
              {currentCompsetName}
            </Typography>
          )}
        </Grid>

        {compsets.loading ? (
          <Skeleton variant="rect" animation="wave" width="25%" height={50} />
        ) : (
          <Autocomplete
            id="compsetList"
            options={compsets.data}
            getOptionLabel={option => option.name}
            style={{ width: 300 }}
            onChange={(e, val) => {
              handleCompsetChange(val as Compset);
            }}
            renderInput={params => <TextField {...params} label="My CompSets" variant="outlined" />}
            autoComplete={true}
            autoHighlight={true}
          />
        )}

        {bookingChanges.loading || compsetListingIDs.loading ? (
          !idle && <Skeleton variant="rect" animation="wave" width="25%" height={50} />
        ) : (
          <Grid>
            <Typography style={{ color: COLOR_GREEN }}>
              Updated {lastUpdateHourDiff} hours {lastUpdateMinuteDiff} minutes ago
            </Typography>
            <Typography style={{ color: COLOR_ACCENT_2 }}>
              Next update in {nextUpdateHourDiff} hours {nextUpdateMinuteDiff} minutes
            </Typography>
          </Grid>
        )}
      </Grid>

      {deleteTriggered && (
        <Grid className={classes.alert}>
          <Alert severity="info" style={{ marginTop: 10 }}>
            CompSet deleted
          </Alert>
        </Grid>
      )}

      {filteredBookings.loading || bookingChanges.loading || compsetListingIDs.loading ? (
        !idle && (
          <Skeleton
            variant="rect"
            animation="wave"
            width="100%"
            height={300}
            className={classes.skeleton}
          />
        )
      ) : (
        <Grid className={classes.rateAnalysisChart}>
          <RateAnalysisChart
            cardTitle="365 Days Available and Booked Daily Rates"
            data={dynamic365DaysAvailAndBookedDailyRates}
            colors={{
              color1: COLOR_ACCENT_1,
              color2: COLOR_ACCENT_2,
            }}
            titles={{
              title1: 'Available Daily Rate',
              title2: 'Booked Daily Rate',
            }}
            isUserAllowed={true}
            isRange={false}
            currency={currency}
          />
        </Grid>
      )}

      {filteredBookings.loading || bookingChanges.loading || compsetListingIDs.loading ? (
        !idle && <Skeleton variant="rect" animation="wave" width="100%" height={600} />
      ) : (
        <>
          <DataGridCard
            cardTitle="Competitive Set"
            rowData={filteredBookings.data}
            colData={bookingChangesCols}
            height={600}
            lastSnapshot={lastSnapshot}
            currentSnapshot={currentSnapshot}
            nextUpdate={nextUpdate}
            customClasses={classes}
            tooltip={true}
            deletedRows={deletedRows}
            setDeletedRows={setDeletedRows}
          />
          <Grid container direction="row" justify="space-between">
            <Button
              className={classes.customButton}
              style={{ background: COLOR_RED }}
              onClick={deleteSelectedRows}
            >
              <DeleteForeverTwoToneIcon />
            </Button>

            <Button className={classes.customButton} onClick={loadAdditionalRows}>
              Load more data
            </Button>
          </Grid>
        </>
      )}

      <Grid
        container
        direction="row"
        justify="space-between"
        className={classes.verticalGraphSection}
      >
        <Grid className={classes.verticlBarCard}>
          {bookingChanges.loading || compsetListingIDs.loading ? (
            !idle && <Skeleton variant="rect" animation="wave" width="100%" height={185} />
          ) : (
            <VerticalBarCard
              title="Available Daily Rate"
              data={dynamicAvailableDailyRates}
              isDisabled={false}
              search={search}
              totalSupply={totalNumberOfListings}
              height={185}
              currency={currency}
            />
          )}
        </Grid>

        <Grid className={classes.verticlBarCard}>
          {bookingChanges.loading || compsetListingIDs.loading ? (
            !idle && <Skeleton variant="rect" animation="wave" width="100%" height={185} />
          ) : (
            <VerticalBarCard
              title="Occupancy Rate"
              data={dynamicOccupancyRates}
              isDisabled={false}
              search={search}
              totalSupply={totalNumberOfListings}
              height={185}
            />
          )}
        </Grid>

        <Grid className={classes.verticlBarCard}>
          {bookingChanges.loading || compsetListingIDs.loading ? (
            !idle && <Skeleton variant="rect" animation="wave" width="100%" height={185} />
          ) : (
            <VerticalBarCard
              title="Booking Lead Time"
              data={dynamicBookingLeads}
              isDisabled={false}
              search={search}
              totalSupply={totalNumberOfListings}
              height={185}
            />
          )}
        </Grid>

        <Grid className={classes.verticlBarCard}>
          {bookingChanges.loading || compsetListingIDs.loading ? (
            !idle && <Skeleton variant="rect" animation="wave" width="100%" height={185} />
          ) : (
            <VerticalBarCard
              title="Average Length of Stay"
              data={dynamicAverageStayLengths}
              isDisabled={false}
              search={search}
              totalSupply={totalNumberOfListings}
              height={185}
            />
          )}
        </Grid>
      </Grid>

      <Grid container direction="row" justify="flex-start">
        {!filteredBookings.loading && !bookingChanges.loading && !compsetListingIDs.loading && (
          <Button
            className={classes.customButton}
            style={{ background: COLOR_RED }}
            onClick={deleteCompset}
          >
            Delete Entire CompSet
          </Button>
        )}
      </Grid>
    </Grid>
  );
};

const getDynamicAvailableAndOccupancyRates = (
  listings: RateAndAvailChanges[],
  dateList: string[]
) => {
  let dynamicAvailableDailyRates: BookingRates = {} as BookingRates;
  let dynamicOccupancyRates: BookingRates = {} as BookingRates;
  let next7DaysAvailableDailyRate: number[] = [];
  let next14DaysAvailableDailyRate: number[] = [];
  let next21DaysAvailableDailyRate: number[] = [];
  let next35DaysAvailableDailyRate: number[] = [];
  let next7DaysBookedDailyRate: number[] = [];
  let next14DaysBookedDailyRate: number[] = [];
  let next21DaysBookedDailyRate: number[] = [];
  let next35DaysBookedDailyRate: number[] = [];

  for (let listing of listings) {
    for (let i = 0; i < 35; i++) {
      const date: string = dateList[i];
      let rate: string = listing[date] as string;
      if (rate.includes('->')) rate = rate.split(' -> ')[1];
      let status: string = '';
      if (rate.includes('b') || rate.includes('rb')) status = 'booked';
      if (rate.includes('a')) status = 'available';

      rate = rate.replace(/:b|:rb|:a|:na/g, '');
      rate = rate.replace('$', '');
      const processedRate: number = parseInt(rate);
      if (status === 'available') {
        if (i < 7) next7DaysAvailableDailyRate.push(processedRate);
        if (i < 14) next14DaysAvailableDailyRate.push(processedRate);
        if (i < 21) next21DaysAvailableDailyRate.push(processedRate);
        if (i < 35) next35DaysAvailableDailyRate.push(processedRate);
      }
      if (status === 'booked') {
        if (i < 7) next7DaysBookedDailyRate.push(processedRate);
        if (i < 14) next14DaysBookedDailyRate.push(processedRate);
        if (i < 21) next21DaysBookedDailyRate.push(processedRate);
        if (i < 35) next35DaysBookedDailyRate.push(processedRate);
      }
    }
  }

  const next7DaysAvgAvailableDailyRate: number =
    next7DaysAvailableDailyRate.length > 0
      ? next7DaysAvailableDailyRate.reduce((a, b) => a + b) / next7DaysAvailableDailyRate.length
      : 0;
  const next14DaysAvgAvailableDailyRate: number =
    next14DaysAvailableDailyRate.length > 0
      ? next14DaysAvailableDailyRate.reduce((a, b) => a + b) / next14DaysAvailableDailyRate.length
      : 0;
  const next21DaysAvgAvailableDailyRate: number =
    next21DaysAvailableDailyRate.length > 0
      ? next21DaysAvailableDailyRate.reduce((a, b) => a + b) / next21DaysAvailableDailyRate.length
      : 0;
  const next35DaysAvgAvailableDailyRate: number =
    next35DaysAvailableDailyRate.length > 0
      ? next35DaysAvailableDailyRate.reduce((a, b) => a + b) / next35DaysAvailableDailyRate.length
      : 0;
  dynamicAvailableDailyRates = {
    '7': Math.round(next7DaysAvgAvailableDailyRate),
    '14': Math.round(next14DaysAvgAvailableDailyRate),
    '21': Math.round(next21DaysAvgAvailableDailyRate),
    '35': Math.round(next35DaysAvgAvailableDailyRate),
  };

  const next7DaysAvgOccupancyRate: number =
    (next7DaysBookedDailyRate.length /
      (next7DaysAvailableDailyRate.length + next7DaysBookedDailyRate.length)) *
    100;
  const next14DaysAvgOccupancyRate: number =
    (next14DaysBookedDailyRate.length /
      (next14DaysAvailableDailyRate.length + next14DaysBookedDailyRate.length)) *
    100;
  const next21DaysAvgOccupancyRate: number =
    (next21DaysBookedDailyRate.length /
      (next21DaysAvailableDailyRate.length + next21DaysBookedDailyRate.length)) *
    100;
  const next35DaysAvgOccupancyRate: number =
    (next35DaysBookedDailyRate.length /
      (next35DaysAvailableDailyRate.length + next35DaysBookedDailyRate.length)) *
    100;
  dynamicOccupancyRates = {
    '7': Math.round(next7DaysAvgOccupancyRate),
    '14': Math.round(next14DaysAvgOccupancyRate),
    '21': Math.round(next21DaysAvgOccupancyRate),
    '35': Math.round(next35DaysAvgOccupancyRate),
  };

  return [dynamicAvailableDailyRates, dynamicOccupancyRates];
};

const getDynamicBookingLeads = (listings: RateAndAvailChanges[]): BookingLeads => {
  let dynamicBookingLeads: BookingLeads = {} as BookingLeads;
  let bookingLeads: number[] = [];

  for (let listing of listings) {
    const bookingLead: number = listing.booking_lead as number;
    bookingLeads.push(bookingLead);
  }

  for (let p = 10; p < 100; p = p + 10) {
    const tempPercentile = d3.quantile(bookingLeads, p / 100);
    dynamicBookingLeads[`${p}`] = Math.round(tempPercentile);
  }

  return dynamicBookingLeads;
};

const getDynamicAverageStays = (listings: RateAndAvailChanges[]): AverageStayLengths => {
  let dynamicAverageStayLengths = {} as AverageStayLengths;
  let averageStayLengths: number[] = [];

  for (let listing of listings) {
    const averageStayLength = listing.average_stay as number;
    averageStayLengths.push(averageStayLength);
  }

  for (let p = 25; p <= 75; p = p + 25) {
    const tempPercentile = d3.quantile(averageStayLengths, p / 100);
    dynamicAverageStayLengths[`${p}`] = Math.round(tempPercentile * 100) / 100;
  }

  return dynamicAverageStayLengths;
};

const getTotalBookingVal = (params: GridCellParams, dateList: string[]): number => {
  let rateList: string[] = [];
  let curCellIndex: number = 0;
  let index: number = 0;
  let totalBookingVal: number = 0;

  for (let date of dateList) {
    if (date === params.field) curCellIndex = index;
    const rate = params.row[date].includes('->')
      ? params.row[date].split(' -> ')[1]
      : params.row[date];
    rateList.push(rate);
    index++;
  }

  for (let i = curCellIndex; i < rateList.length; i++) {
    if (rateList[i].includes('b') || rateList[i].includes('rb')) {
      let tempBookingVal: string = rateList[i].replace(/:b|:rb|:a|:na/g, '');
      tempBookingVal = tempBookingVal.replace('$', '');
      totalBookingVal += parseInt(tempBookingVal);
    } else break;
  }

  if (curCellIndex > 0) {
    for (let i = curCellIndex - 1; i >= 0; i--) {
      if (rateList[i].includes('b') || rateList[i].includes('rb')) {
        let tempBookingVal: string = rateList[i].replace(/:b|:rb|:a|:na/g, '');
        tempBookingVal = tempBookingVal.replace('$', '');
        totalBookingVal += parseInt(tempBookingVal);
      } else break;
    }
  }

  return totalBookingVal;
};

const getDynamic365DaysAvailAndBookedDailyRates = (
  listings: RateAndAvailChanges[],
  dateList: string[],
  currency: string
): PacingData[] => {
  let dynamic365DaysAvailAndBookedDailyRates: PacingData[] = [];
  dateList.forEach(date => {
    let bookedRates: number[] = [];
    let availableRates: number[] = [];
    listings.forEach(listing => {
      const rate: string = listing[date] as string;
      if (rate.includes(':b') || rate.includes(':rb')) {
        let bookedRate: string = rate.replace(/:b|:rb|:a|:na/g, '');
        bookedRate = bookedRate.includes('->') ? bookedRate.split(' -> ')[1] : bookedRate;
        bookedRate = bookedRate.replace('$', '');
        bookedRates.push(parseInt(bookedRate));
      }
      if (rate.includes(':a')) {
        let availableRate: string = rate.replace(/:a|:na/g, '');
        availableRate = availableRate.includes('->')
          ? availableRate.split(' -> ')[1]
          : availableRate;
        availableRate = availableRate.replace('$', '');
        availableRates.push(parseInt(availableRate));
      }
    });
    dynamic365DaysAvailAndBookedDailyRates.push({
      'Available Daily Rate': convertCurrency(currency, Math.round(d3.mean(availableRates))),
      'Booked Daily Rate': convertCurrency(currency, Math.round(d3.mean(bookedRates))),
      date: date,
    });
  });

  return dynamic365DaysAvailAndBookedDailyRates;
};

const parsePrice = (currency: string, price: string): string => {
  const currencySymbol: string = exchangeRates[currency].symbol;
  if (price.includes('->')) {
    const prices: string[] = price.split(' -> ');
    const prevPrice: string = prices[0].replace('$', '');
    const nextPrice: string = prices[1].replace('$', '');
    const convertedPrevPrice: number = convertCurrency(currency, parseInt(prevPrice));
    const convertedNextPrice: number = convertCurrency(currency, parseInt(nextPrice));
    return `${currencySymbol}${convertedPrevPrice} -> ${currencySymbol}${convertedNextPrice}`;
  } else {
    const convertedPrice = convertCurrency(currency, parseInt(price.replace('$', '')));
    return `${currencySymbol}${convertedPrice}`;
  }
};

export default ListingView;
